import React, { useState } from 'react';
import {
  FinancialAssets,
  FinancialLiabilities,
  FinancialCashFlow,
  CalculatedSummary,
  formatCurrency,
  getCurrencySymbol,
  ALL_CURRENCIES,
  WEALTH_TIER_CONFIG
} from '../utils/financeCalculations';
import './FinanceInputPanel.css';

interface FinanceInputPanelProps {
  assets: FinancialAssets;
  liabilities: FinancialLiabilities;
  cashFlow: FinancialCashFlow;
  currency: string;
  calculated: CalculatedSummary;
  saving: boolean;
  hasUnsavedChanges: boolean;
  onAssetsChange: (assets: FinancialAssets) => void;
  onLiabilitiesChange: (liabilities: FinancialLiabilities) => void;
  onCashFlowChange: (cashFlow: FinancialCashFlow) => void;
  onCurrencyChange: (currency: string) => void;
  onSave: () => void;
  onReset: () => void;
  onOpenCustomizer: () => void;
  publicUsername?: string;
}

export const FinanceInputPanel: React.FC<FinanceInputPanelProps> = ({
  assets,
  liabilities,
  cashFlow,
  currency,
  calculated,
  saving,
  hasUnsavedChanges,
  onAssetsChange,
  onLiabilitiesChange,
  onCashFlowChange,
  onCurrencyChange,
  onSave,
  onReset,
  onOpenCustomizer,
  publicUsername
}) => {
  const [activeTab, setActiveTab] = useState<'summary' | 'assets' | 'liabilities' | 'cashflow'>('summary');
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const MAX_INPUT_CEILING = 1e14; // 100 trillion allowable limit to support multi-currency values

  const handleAssetField = (field: keyof FinancialAssets, valStr: string) => {
    if (valStr === '') {
      setValidationErrors((prev) => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
      onAssetsChange({ ...assets, [field]: 0 });
      return;
    }
    const num = parseFloat(valStr);
    if (isNaN(num) || num < 0) {
      setValidationErrors((prev) => ({ ...prev, [field]: 'Must be a valid positive number' }));
      return;
    }
    if (num > MAX_INPUT_CEILING) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: 'Capped at maximum limit (100 trillion)'
      }));
      onAssetsChange({ ...assets, [field]: MAX_INPUT_CEILING });
      return;
    }
    setValidationErrors((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
    onAssetsChange({ ...assets, [field]: num });
  };

  const handleLiabilityField = (field: keyof FinancialLiabilities, valStr: string) => {
    if (valStr === '') {
      setValidationErrors((prev) => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
      onLiabilitiesChange({ ...liabilities, [field]: 0 });
      return;
    }
    const num = parseFloat(valStr);
    if (isNaN(num) || num < 0) {
      setValidationErrors((prev) => ({ ...prev, [field]: 'Must be a valid positive number' }));
      return;
    }
    if (num > MAX_INPUT_CEILING) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: 'Capped at maximum limit (100 trillion)'
      }));
      onLiabilitiesChange({ ...liabilities, [field]: MAX_INPUT_CEILING });
      return;
    }
    setValidationErrors((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
    onLiabilitiesChange({ ...liabilities, [field]: num });
  };

  const handleCashFlowField = (field: keyof FinancialCashFlow, valStr: string) => {
    if (valStr === '') {
      setValidationErrors((prev) => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
      onCashFlowChange({ ...cashFlow, [field]: 0 });
      return;
    }
    const num = parseFloat(valStr);
    if (isNaN(num) || num < 0) {
      setValidationErrors((prev) => ({ ...prev, [field]: 'Must be a valid positive number' }));
      return;
    }
    if (num > MAX_INPUT_CEILING) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: 'Capped at maximum limit (100 trillion)'
      }));
      onCashFlowChange({ ...cashFlow, [field]: MAX_INPUT_CEILING });
      return;
    }
    setValidationErrors((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
    onCashFlowChange({ ...cashFlow, [field]: num });
  };

  const effectiveTier = calculated.hasData ? calculated.visualizationTier : 'empty';
  const tierInfo = WEALTH_TIER_CONFIG[effectiveTier] || WEALTH_TIER_CONFIG.empty;

  return (
    <div className="finance-panel-container">
      {/* Privacy Guarantee Banner */}
      <div className="privacy-badge-banner" role="status">
        <span className="shield-icon" aria-hidden="true">🛡️</span>
        <div className="privacy-text">
          <strong>Strict Privacy Guarantee:</strong> Your financial values, assets, debts, and tier
          remain 100% private to your authenticated session. Only your customized 3D character and
          decorative house style appear on your public profile.
        </div>
      </div>

      {/* Financial Snapshot Summary Metrics */}
      <div className="finance-metrics-grid">
        <div className="metric-card net-worth-card">
          <span className="metric-label">Net Worth</span>
          <div
            className={`metric-value ${calculated.netWorth >= 0 ? 'text-positive' : 'text-negative'}`}
          >
            {formatCurrency(calculated.netWorth, currency)}
          </div>
          <span className="metric-subtext">Total Assets − Total Liabilities</span>
        </div>

        <div className="metric-card">
          <span className="metric-label">Total Assets</span>
          <div className="metric-value text-positive">
            {formatCurrency(calculated.totalAssets, currency)}
          </div>
          <span className="metric-subtext">6 Diversified Categories</span>
        </div>

        <div className="metric-card">
          <span className="metric-label">Total Liabilities</span>
          <div className="metric-value text-liability">
            {formatCurrency(calculated.totalLiabilities, currency)}
          </div>
          <span className="metric-subtext">Debts & Credit Balances</span>
        </div>

        <div className="metric-card">
          <span className="metric-label">Monthly Surplus</span>
          <div
            className={`metric-value ${calculated.monthlySurplus >= 0 ? 'text-positive' : 'text-negative'}`}
          >
            {formatCurrency(calculated.monthlySurplus, currency)}
          </div>
          <span className="metric-subtext">Income − Expenses / month</span>
        </div>

        <div className="metric-card tier-card">
          <span className="metric-label">Private 3D World Tier</span>
          <div className="tier-display" style={{ color: tierInfo.accentColor }}>
            <span className="tier-icon">{effectiveTier === 'empty' ? '🌱' : '🏰'}</span>
            <span className="tier-name">{tierInfo.name}</span>
          </div>
          <span className="metric-subtext">{tierInfo.badgeLabel}</span>
        </div>
      </div>

      {/* Controls Bar: Currency Selector & Navigation */}
      <div className="finance-controls-bar">
        <div className="finance-currency-bar-item">
          <label htmlFor="finance-currency-select" className="currency-selector-label">
            💱 Currency:
          </label>
          <select
            id="finance-currency-select"
            value={currency}
            onChange={(e) => onCurrencyChange(e.target.value)}
            className="currency-dropdown global-currency-select"
            title="Preferred Currency (saved across sessions)"
            aria-label="Select preferred currency"
          >
            {ALL_CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} ({c.symbol}) — {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Tabs navigation */}
        <div className="finance-tabs-nav" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'summary'}
            className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
          >
            📊 Overview
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'assets'}
            className={`tab-btn ${activeTab === 'assets' ? 'active' : ''}`}
            onClick={() => setActiveTab('assets')}
          >
            💎 Assets ({formatCurrency(calculated.totalAssets, currency)})
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'liabilities'}
            className={`tab-btn ${activeTab === 'liabilities' ? 'active' : ''}`}
            onClick={() => setActiveTab('liabilities')}
          >
            💳 Liabilities ({formatCurrency(calculated.totalLiabilities, currency)})
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'cashflow'}
            className={`tab-btn ${activeTab === 'cashflow' ? 'active' : ''}`}
            onClick={() => setActiveTab('cashflow')}
          >
            💰 Cash Flow
          </button>
        </div>
      </div>

      {/* Tab Panels */}
      <div className="tab-panel-body">
        {/* TAB 1: OVERVIEW & CONTROLS */}
        {activeTab === 'summary' && (
          <div className="overview-tab-content">
            <div className="overview-controls-bar">
              <div className="currency-selector-group">
                <label htmlFor="currency-select">Base Currency:</label>
                <select
                  id="currency-select"
                  value={currency}
                  onChange={(e) => onCurrencyChange(e.target.value)}
                  className="currency-dropdown"
                >
                  {ALL_CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} ({c.symbol}) — {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="overview-action-buttons">
                {publicUsername && (
                  <a
                    href={`/profile/${publicUsername}`}
                    target="_blank"
                    rel="noreferrer"
                    className="action-btn secondary-btn"
                    title="View how other visitors see your public character and house"
                  >
                    🌐 Public Profile
                  </a>
                )}
                <button
                  type="button"
                  className="action-btn secondary-btn"
                  onClick={onOpenCustomizer}
                >
                  🎨 Customize Character
                </button>
              </div>
            </div>

            <div className="tier-breakdown-card" style={{ borderColor: tierInfo.accentColor }}>
              <div className="tier-breakdown-header">
                <div>
                  <span className="tier-tag" style={{ backgroundColor: tierInfo.accentColor }}>
                    {tierInfo.badgeLabel}
                  </span>
                  <h4 className="tier-breakdown-title">{tierInfo.name}</h4>
                  <p className="tier-breakdown-desc">
                    {tierInfo.description}
                  </p>
                </div>
              </div>
              <div className="room-perks-list">
                <div className="perk-item">
                  <span className="perk-icon">{effectiveTier === 'empty' ? '🌱' : '🏡'}</span>
                  <span>Layout: <strong>{effectiveTier === 'empty' ? 'OPEN GROUND PLOT' : tierInfo.roomTheme.roomLayout.toUpperCase()}</strong></span>
                </div>
                <div className="perk-item">
                  <span className="perk-icon">🛋️</span>
                  <span>Furniture: <strong>{effectiveTier === 'empty' ? 'No Furnishings (Enter wealth data to build)' : 'Customized to Tier'}</strong></span>
                </div>
                <div className="perk-item">
                  <span className="perk-icon">💡</span>
                  <span>Lighting: <strong>{effectiveTier === 'empty' ? 'NATURAL SUNLIGHT' : tierInfo.roomTheme.lightingMood.toUpperCase()}</strong></span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: ASSETS FORM */}
        {activeTab === 'assets' && (
          <div className="form-grid-section">
            <div className="form-field-group">
              <label htmlFor="field-cashAndSavings">Cash & Bank Savings</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-cashAndSavings"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="100"
                  value={assets.cashAndSavings || ''}
                  onChange={(e) => handleAssetField('cashAndSavings', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.cashAndSavings && (
                <span className="field-error">{validationErrors.cashAndSavings}</span>
              )}
            </div>

            <div className="form-field-group">
              <label htmlFor="field-investments">Stocks, Funds & Crypto</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-investments"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="100"
                  value={assets.investments || ''}
                  onChange={(e) => handleAssetField('investments', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.investments && (
                <span className="field-error">{validationErrors.investments}</span>
              )}
            </div>

            <div className="form-field-group">
              <label htmlFor="field-retirementAccounts">Retirement Accounts (401k/IRA/PF)</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-retirementAccounts"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="100"
                  value={assets.retirementAccounts || ''}
                  onChange={(e) => handleAssetField('retirementAccounts', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.retirementAccounts && (
                <span className="field-error">{validationErrors.retirementAccounts}</span>
              )}
            </div>

            <div className="form-field-group">
              <label htmlFor="field-propertyValue">Real Estate & Property Value</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-propertyValue"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="1000"
                  value={assets.propertyValue || ''}
                  onChange={(e) => handleAssetField('propertyValue', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.propertyValue && (
                <span className="field-error">{validationErrors.propertyValue}</span>
              )}
            </div>

            <div className="form-field-group">
              <label htmlFor="field-vehicles">Vehicles & Transport</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-vehicles"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="500"
                  value={assets.vehicles || ''}
                  onChange={(e) => handleAssetField('vehicles', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.vehicles && (
                <span className="field-error">{validationErrors.vehicles}</span>
              )}
            </div>

            <div className="form-field-group">
              <label htmlFor="field-otherAssets">Other Assets & Collectibles</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-otherAssets"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="100"
                  value={assets.otherAssets || ''}
                  onChange={(e) => handleAssetField('otherAssets', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.otherAssets && (
                <span className="field-error">{validationErrors.otherAssets}</span>
              )}
            </div>
          </div>
        )}

        {/* TAB 3: LIABILITIES FORM */}
        {activeTab === 'liabilities' && (
          <div className="form-grid-section">
            <div className="form-field-group">
              <label htmlFor="field-creditCardDebt">Credit Card Balances</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-creditCardDebt"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="50"
                  value={liabilities.creditCardDebt || ''}
                  onChange={(e) => handleLiabilityField('creditCardDebt', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.creditCardDebt && (
                <span className="field-error">{validationErrors.creditCardDebt}</span>
              )}
            </div>

            <div className="form-field-group">
              <label htmlFor="field-studentLoans">Student / Education Loans</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-studentLoans"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="500"
                  value={liabilities.studentLoans || ''}
                  onChange={(e) => handleLiabilityField('studentLoans', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.studentLoans && (
                <span className="field-error">{validationErrors.studentLoans}</span>
              )}
            </div>

            <div className="form-field-group">
              <label htmlFor="field-homeLoans">Home Loan / Mortgage</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-homeLoans"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="1000"
                  value={liabilities.homeLoans || ''}
                  onChange={(e) => handleLiabilityField('homeLoans', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.homeLoans && (
                <span className="field-error">{validationErrors.homeLoans}</span>
              )}
            </div>

            <div className="form-field-group">
              <label htmlFor="field-otherDebts">Personal & Other Debts</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-otherDebts"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="100"
                  value={liabilities.otherDebts || ''}
                  onChange={(e) => handleLiabilityField('otherDebts', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.otherDebts && (
                <span className="field-error">{validationErrors.otherDebts}</span>
              )}
            </div>
          </div>
        )}

        {/* TAB 4: CASH FLOW FORM */}
        {activeTab === 'cashflow' && (
          <div className="form-grid-section">
            <div className="form-field-group">
              <label htmlFor="field-monthlyIncome">Monthly Net Income</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-monthlyIncome"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="100"
                  value={cashFlow.monthlyIncome || ''}
                  onChange={(e) => handleCashFlowField('monthlyIncome', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.monthlyIncome && (
                <span className="field-error">{validationErrors.monthlyIncome}</span>
              )}
            </div>

            <div className="form-field-group">
              <label htmlFor="field-monthlyExpenses">Monthly Total Expenses</label>
              <div className="input-currency-wrapper">
                <span className="currency-prefix" title={currency}>{getCurrencySymbol(currency)}</span>
                <input
                  id="field-monthlyExpenses"
                  type="number"
                  min="0"
                  max={MAX_INPUT_CEILING}
                  step="100"
                  value={cashFlow.monthlyExpenses || ''}
                  onChange={(e) => handleCashFlowField('monthlyExpenses', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.monthlyExpenses && (
                <span className="field-error">{validationErrors.monthlyExpenses}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer Controls with Save / Reset / Unsaved Warning */}
      <div className="panel-footer-actions">
        {hasUnsavedChanges && (
          <div className="unsaved-warning-pill">
            <span className="dot-pulse" /> Unsaved changes in draft
          </div>
        )}

        <div className="action-buttons-group">
          <button
            type="button"
            className="action-btn cancel-btn"
            onClick={onReset}
            disabled={saving || !hasUnsavedChanges}
          >
            Cancel / Reset
          </button>

          <button
            type="button"
            className="action-btn save-btn"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? (
              <span className="btn-spinner-content">
                <span className="btn-spinner" /> Saving...
              </span>
            ) : (
              '💾 Save Financial Snapshot'
            )}
          </button>
        </div>
      </div>

      <div className="disclaimer-note">
        💡 Note: My Wealth provides private visual representation and personal budget tracking. It
        does not provide financial advice, loan recommendations, or investment endorsements.
      </div>
    </div>
  );
};

export default FinanceInputPanel;
